import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { MessageCircle, X, Loader2 } from 'lucide-react';
import { Button } from './ui/Button';
import { feedbackAPI } from '../api';
import { useToast } from '../hooks/useToast';

const FEEDBACK_TYPES = [
  { value: 'comment_or_question', label: 'Comment or question' },
  { value: 'bug_report', label: 'Bug report' },
  { value: 'feature_idea', label: 'Feature idea' },
];

const DISMISSED_KEY = 'feedback-fab-dismissed';

/**
 * FeedbackFab - Floating action button that opens a feedback modal.
 * Shown in admin and tenant layouts; users can submit feedback (comment, bug, feature idea).
 */
const FeedbackFab = () => {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      if (dismissed) {
        sessionStorage.setItem(DISMISSED_KEY, 'true');
      } else {
        sessionStorage.removeItem(DISMISSED_KEY);
      }
    } catch {
      /* ignore */
    }
  }, [dismissed]);

  const handleDismiss = (e) => {
    e.stopPropagation();
    setDismissed(true);
  };
  const [message, setMessage] = useState('');
  const [type, setType] = useState('comment_or_question');
  const [sourcePage, setSourcePage] = useState(null); // page when modal was opened
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { success, error: toastError, ToastContainer } = useToast();

  const handleOpen = () => {
    setSourcePage(location.pathname || null);
    setOpen(true);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setOpen(false);
      setMessage('');
      setType('comment_or_question');
      setSourcePage(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = (message || '').trim();
    if (!trimmed) {
      toastError('Message required', 'Please enter your feedback.');
      return;
    }
    setIsSubmitting(true);
    try {
      await feedbackAPI.submit({ message: trimmed, type, sourcePage: sourcePage || undefined });
      success('Feedback sent', 'Thank you for your feedback.');
      handleClose();
    } catch (err) {
      toastError('Failed to send', err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {!dismissed && (
        <div className="fixed bottom-6 right-6 z-40">
          <div className="relative">
            <button
              type="button"
              onClick={handleOpen}
              className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--ds-accent-primary)] text-white shadow-lg transition-colors hover:bg-[var(--ds-accent-primary-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--ds-accent-focus-ring)] focus:ring-offset-2 focus:ring-offset-[var(--ds-surface-page)]"
              aria-label="Send feedback"
            >
              <MessageCircle className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="absolute -top-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-gray-700 text-white shadow-sm transition-opacity hover:bg-gray-600 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-[var(--ds-accent-focus-ring)] dark:bg-gray-600 dark:hover:bg-gray-500"
              aria-label="Dismiss feedback button"
            >
              <X className="h-3 w-3" strokeWidth={2.5} />
            </button>
          </div>
        </div>
      )}

      <Dialog.Root
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setMessage('');
            setType('comment_or_question');
            setSourcePage(null);
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
          <Dialog.Content className="fixed top-1/2 left-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-lg border border-[var(--ds-border-subtle)] bg-white shadow-[var(--ds-shadow-popover)] dark:bg-gray-800">
            <form onSubmit={handleSubmit}>
              <div className="border-b border-gray-200 px-6 py-4 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Send feedback
                  </Dialog.Title>
                  <Dialog.Close asChild>
                    <button
                      type="button"
                      className="rounded-md p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                      aria-label="Close"
                      disabled={isSubmitting}
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </Dialog.Close>
                </div>
              </div>

              <div className="space-y-4 px-6 py-5">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  We'd love to hear your feedback! Please let us know what you think or if you have any issues.
                </p>

                <div>
                  <label
                    htmlFor="feedback-type"
                    className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Type
                  </label>
                  <div
                    id="feedback-type"
                    className="flex flex-wrap gap-2"
                    role="group"
                    aria-label="Feedback type"
                  >
                    {FEEDBACK_TYPES.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setType(opt.value)}
                        className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--ds-accent-focus-ring)] ${
                          type === opt.value
                            ? 'border-[var(--ds-accent-primary)] bg-[var(--ds-accent-primary)] text-white dark:border-[var(--ds-accent-primary)]'
                            : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="feedback-message"
                    className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="feedback-message"
                    required
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Your feedback..."
                    rows={4}
                    className="w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-[var(--ds-accent-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--ds-accent-focus-ring)] dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-800/80">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Submit'
                  )}
                </Button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ToastContainer />
    </>
  );
};

export default FeedbackFab;
